import {DashboardDataSource} from 'store/modules/dashboards/dataGetter/DashboardDataSource';
import wrapArray from 'utils/array/wrapArray';
import _ from 'lodash';
import getRequestParamsForWidget from 'store/modules/dashboards/dataGetter/getRequestParamsForWidget';
import postprocessWidgetData from 'store/modules/dashboards/dataGetter/postprocessWidgetData';
import tracker from 'tracking';

_.noConflict();

const GROUP_FIELDS = [
  'id',
  'channelIds',
  'channels',
  'grouping',
  'dateEnd',
  'dateStart',
  'dateGrouping',
  'filterEntities',
  'filteringType',
  'filteringProjects',
  'profiles',
  'goals',
  'sorting',
  'orderGrouping'
];
const CONCAT_FIELDS = ['metrics', 'metricsList', 'metric'];

const GROUPING_COLUMNS_MAP = {
  placement: ['placement_name', 'placement_id'],
  order: ['order_id', 'order_name'],
  channel: ['channel_id'],
  project: ['project_id', 'project_name'],
  label: ['label_name']
};

/**
 * Returns grouping key and args for widget based on GROUP_FIELDS
 * fields that are used to group API requests
 */
function getGroupHashForRequest (dsType, dataParams) {
  const dsUniqueParams = _.pick(dataParams, GROUP_FIELDS);

  const uniqueHash = GROUP_FIELDS.map(i => {
    const f = dsUniqueParams[i];
    if (!f) {
      return '*';
    }
    if (typeof f === 'object') {
      return JSON.stringify(f);
    }
    return f;
  }).join('-');

  const params = {
    ...dsUniqueParams,
    ..._.mapValues(_.pick(dataParams, CONCAT_FIELDS), i => wrapArray(i))
  };

  return [`${dsType}_${uniqueHash}`, params];
}

function prepareRequestParams (dsType, requestParams) {
  if (dsType === 'orders') {
    return requestParams;
  }
  const metrics = new Set(requestParams.metrics);
  if (requestParams.dateGrouping !== 'no') {
    metrics.add('date');
  }
  if (requestParams.grouping) {
    const requiredColumns = GROUPING_COLUMNS_MAP[requestParams.grouping] || [];
    requiredColumns.forEach(i => metrics.add(i));
  }

  if (requestParams.planFactMetrics) {
    requestParams.planFactMetrics.forEach(m => {
      metrics.add(m.metric);
    });
  }

  return {
    ...requestParams,
    metrics: Array.from(metrics)
  };
}

async function getDashboardData (dashboard, compiledWidgets, dashboardParams, profiles, goals,
  withVat, dashboardMetrics, ignoreCache = false) {
  const goalsMap = _.keyBy(goals, 'id');
  const profilesMap = _.keyBy(profiles, 'id');
  const dashboardDataSource = new DashboardDataSource(
    dashboard,
    dashboardParams,
    [],
    dashboardMetrics
  );

  const groupedDataRequests = {};
  const widgetIdRequestMap = {};
  const requestDsTypeMap = {};

  compiledWidgets.forEach(widget => {
    const widgetParams = getRequestParamsForWidget(widget, dashboardParams, profilesMap, goalsMap);

    Object.keys(widgetParams).
      forEach(key => {

        // key === statistics/key === orders
        widgetParams[key].forEach(r => {
          const [hashKey, requestParams] = getGroupHashForRequest(key, r);
          requestDsTypeMap[hashKey] = key;

          if (!widgetIdRequestMap[widget.id]) {
            widgetIdRequestMap[widget.id] = [];
          }
          widgetIdRequestMap[widget.id].push(hashKey);

          if (groupedDataRequests[hashKey]) {
            const statObj = groupedDataRequests[hashKey];
            CONCAT_FIELDS.forEach(f => {
              if (!requestParams[f]) {
                return;
              }
              statObj[f] = [...statObj[f], ...requestParams[f]];
            });

          } else {
            groupedDataRequests[hashKey] = requestParams;
          }
        });
      });
  });


  const requestPromises = {};

  Object.keys(groupedDataRequests).forEach(r => {
    const dsType = requestDsTypeMap[r];
    const requestParams = prepareRequestParams(dsType, groupedDataRequests[r]);

    requestPromises[r] = {
      requestPromise: dashboardDataSource.fetchData(
        dsType,
        requestParams,
        dashboardParams.accountsFiltration,
        dashboardParams.projectsFiltration,
        withVat,
        ignoreCache
      )
    };
  });

  const widgetPromises = {};

  compiledWidgets.forEach(widget => {
    const startTimestamp = new Date();
    const getWidgetData = async () => {
      const widgetRequests = widgetIdRequestMap[widget.id];
      if (!widgetRequests) {
        return;
      }
      const widgetDataPromises = widgetRequests.map(i => requestPromises[i].requestPromise);
      const data = await Promise.all(widgetDataPromises);
      const endTimestamp = new Date();
      const loadingTime = (endTimestamp - startTimestamp) / 1000;

      if (dashboard.id) {
        tracker.track('widget_data_received', {type: widget.type, time: loadingTime, source: widget.dsType});
      } else {
        tracker.track('project_widget_data_received', {type: widget.type, time: loadingTime, source: widget.dsType});
      }

      return postprocessWidgetData(widget, data);
    };

    widgetPromises[widget.id] = getWidgetData();
  });

  return widgetPromises;
}


export default getDashboardData;
